// Dashboard (/) — Executive Overview.
//
// Department-wide snapshot. Every tile below reads an aggregate key that has
// been verified to exist in `dash_aggregates` as of 2026-04-19. Keys that
// aren't yet instrumented are represented as "Not yet tracked" cards rather
// than silently rendering 0/— with a fake label (per the validation rule).
//
// Bands, top → bottom:
//   1. Signal tiles       — six big-number metrics across the org
//   2. AI insight cards   — "what changed today" from the nightly engine
//   3. Committee grid     — one tile per committee with a link into that section
//   4. Data freshness     — per-source SLO status
//
// STALENESS RULE (Dale, 2026-08-26): `dash_aggregates` is written by an
// off-repo runner and can freeze without emptying. When it freezes, the daily
// history snapshot copies the frozen values forward, so within ~28 days
// current === prior for every key and `deltaTrend` returns undefined — which
// this page used to render as a clean tile with no arrow, i.e. "no change".
// That is a lie: nothing changed because nobody wrote, not because the
// department stood still. So trends are gated on `freshness.is_stale` from the
// API, and when the layer is stale the page says so with the as-of date. The
// NUMBERS still render — a stale count is still the last known count — only
// the trend and its "no change" implication are withdrawn.

import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAggregates } from "../hooks/useDashboard";
import type { AggregatesResponse } from "../hooks/useDashboard";
import { useInsights, useRiskSummary, usePipelineStalls } from "../hooks/useAI";
import InsightCards from "../components/ai/InsightCards";
import KPICard from "../components/dashboard/KPICard";
import Banner from "../components/shared/Banner";
import { LiveIndicator } from "../components/shared/LiveIndicator";

// ── helpers ──────────────────────────────────────────────────────────────────

/** Parse a dash_aggregates value to a number, returning null (not 0) when absent. */
function numOrNull(v: unknown): number | null {
  if (typeof v === "number" && Number.isFinite(v)) return v;
  if (typeof v === "string") {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function fmtNum(n: number | null, fallback = "Not yet tracked"): string {
  return n == null ? fallback : n.toLocaleString();
}

function fmtPct(n: number | null, fallback = "Not yet tracked"): string {
  if (n == null) return fallback;
  // dash_aggregates stores rates as 0..1. Convert to percent.
  const pct = n <= 1 ? n * 100 : n;
  return `${pct.toFixed(1)}%`;
}

// ── KPI trend gating ─────────────────────────────────────────────────────────

export type KpiTrend = {
  direction: "up" | "down";
  label: string;
  positiveIsGood?: boolean;
};

/**
 * Turns (current, prior) into a KPICard trend prop. Returns undefined when
 * either side is null or the change is below the noise floor.
 *
 * CALLERS MUST GATE THIS ON FRESHNESS. `undefined` here means "no arrow to
 * draw" and says nothing about why — on a frozen source it fires for every
 * key, forever, and the resulting bare tile reads as "no change".
 */
export function deltaTrend(
  current: number | null,
  priorVal: number | null,
  opts: {
    format?: "integer" | "percent_point" | "percent_relative";
    positiveIsGood?: boolean;
  } = {},
): KpiTrend | undefined {
  if (current == null || priorVal == null) return undefined;
  const diff = current - priorVal;
  if (Math.abs(diff) < 1e-6) return undefined;
  const fmt = opts.format ?? "integer";
  let label = "";
  if (fmt === "percent_point") {
    label = `${diff >= 0 ? "+" : ""}${(diff * 100).toFixed(1)}pp`;
  } else if (fmt === "percent_relative") {
    const rel = priorVal !== 0 ? (diff / Math.abs(priorVal)) * 100 : 0;
    label = `${rel >= 0 ? "+" : ""}${rel.toFixed(0)}%`;
  } else {
    label = `${diff >= 0 ? "+" : ""}${Math.round(diff)}`;
  }
  return {
    direction: diff > 0 ? "up" : "down",
    label,
    positiveIsGood: opts.positiveIsGood ?? true,
  };
}

export type KpiTrendStatus = "loading" | "fresh" | "stale";

export interface KpiTrendState {
  status: KpiTrendStatus;
  /** Whether trend arrows may be drawn at all. False unless status is 'fresh'. */
  showTrends: boolean;
  /** MAX(updated_at) as the worker holds it, e.g. "2026-08-07 14:13:47". */
  asOf: string | null;
  /** Date portion of asOf — what the banner shows. Null when unknown. */
  asOfDay: string | null;
  /** Whole days since asOf. Null when asOf is absent or unreadable. */
  ageDays: number | null;
  /** Declared refresh SLO in hours, when the worker reported one. */
  sloHours: number | null;
  /** Banner copy. Null when there is nothing to warn about. */
  message: string | null;
}

/** "2026-08-07 14:13:47" -> "2026-08-07". Returns null if it isn't a date. */
export function asOfDayOf(asOf: string | null | undefined): string | null {
  if (!asOf) return null;
  const m = /^(\d{4}-\d{2}-\d{2})/.exec(asOf.trim());
  return m ? m[1] : null;
}

/**
 * Decide what the KPI band is allowed to claim.
 *
 * Three states, deliberately distinct:
 *   loading — no response yet. No trends, no banner; the tiles already read
 *             "Not yet tracked". Silence here is correct, not a lie.
 *   stale   — the aggregate layer has not been rewritten within 2x its SLO,
 *             or reports no readable timestamp. Numbers still render; trends
 *             are withdrawn and the as-of date is stated.
 *   fresh   — normal behavior. A flat KPI genuinely means no change.
 *
 * A response that arrives WITHOUT a `freshness` block is treated as stale.
 * Unknown freshness is not evidence of freshness, and defaulting the other way
 * is the exact mistake this function exists to prevent.
 */
export function aggregatesTrendState(
  agg: AggregatesResponse | null | undefined,
  nowMs: number = Date.now(),
): KpiTrendState {
  if (!agg) {
    return {
      status: "loading",
      showTrends: false,
      asOf: null,
      asOfDay: null,
      ageDays: null,
      sloHours: null,
      message: null,
    };
  }

  const f = agg.freshness;
  const asOf = f?.updated_at ?? agg.updated_at ?? null;
  const asOfDay = asOfDayOf(asOf);
  const sloHours =
    f && Number.isFinite(f.slo_hours) && f.slo_hours > 0 ? f.slo_hours : null;

  let ageDays: number | null = null;
  if (f && f.age_hours != null && Number.isFinite(f.age_hours)) {
    ageDays = Math.floor(f.age_hours / 24);
  } else if (asOfDay) {
    // Fall back to the date the worker gave us rather than showing nothing.
    const ms = Date.parse(`${asOfDay}T00:00:00Z`);
    if (Number.isFinite(ms)) {
      ageDays = Math.max(0, Math.floor((nowMs - ms) / 86_400_000));
    }
  }

  const isStale = f?.is_stale ?? true;
  if (!isStale) {
    return {
      status: "fresh",
      showTrends: true,
      asOf,
      asOfDay,
      ageDays,
      sloHours,
      message: null,
    };
  }

  const sloNote = sloHours != null ? ` The refresh SLO is ${sloHours}h.` : "";
  const ageNote = ageDays != null ? ` (${ageDays} days ago)` : "";
  const message = asOfDay
    ? `NO DATA - these KPIs are STALE AS OF ${asOfDay}${ageNote}. ` +
      `The figures below are the last values written, not current ones.${sloNote} ` +
      `Trend comparisons are withheld: with no new writes a flat KPI would mean nobody measured, not that nothing changed.`
    : `NO DATA - the KPI layer reports no readable last-written timestamp, so its age cannot be established.${sloNote} ` +
      `The figures below are last-known values and trend comparisons are withheld.`;

  return {
    status: "stale",
    showTrends: false,
    asOf,
    asOfDay,
    ageDays,
    sloHours,
    message,
  };
}

/**
 * The small print under a KPI value that explains what it is compared against.
 * On a stale layer this must NOT say "vs <date>" — there is no live comparison
 * happening, and naming a date implies one.
 */
export function comparisonNote(
  state: KpiTrendState,
  priorSnapshotDay: string | null,
): string | null {
  if (state.status === "stale") {
    return state.asOfDay ? `stale as of ${state.asOfDay}` : "stale - no trend";
  }
  if (state.status === "fresh" && priorSnapshotDay) {
    return `vs ${priorSnapshotDay}`;
  }
  return null;
}

/** Join subtitle fragments, dropping the empty ones. */
export function subtitleOf(
  ...parts: Array<string | null | undefined>
): string | undefined {
  const kept = parts.filter((p): p is string => !!p);
  return kept.length ? kept.join(" · ") : undefined;
}

// ── band 1: signal tiles ─────────────────────────────────────────────────────

export const AGG_STALE_BANNER_ID = "dashboard-aggregates-stale";

export interface SignalTilesProps {
  trendState: KpiTrendState;
  priorSnapshotDay: string | null;
  /** From dash_aggregates — subject to trendState. */
  activeMembers: number | null;
  totalMembers: number | null;
  priorActiveMembers: number | null;
  applicantsInPipeline: number | null;
  applicantsYtd: number | null;
  priorApplicantsPipeline: number | null;
  firstYearRetention: number | null;
  retentionCohortSize: number | null;
  priorRetention: number | null;
  /** From the AI + ops endpoints — NOT from dash_aggregates, never gated. */
  criticalRiskCount: number;
  pipelineStallCount: number;
  freshnessBreachCount: number;
}

/**
 * The six top-line tiles, extracted so the staleness gate is testable without
 * mounting the whole page and its five polling hooks.
 */
export function SignalTiles({
  trendState,
  priorSnapshotDay,
  activeMembers,
  totalMembers,
  priorActiveMembers,
  applicantsInPipeline,
  applicantsYtd,
  priorApplicantsPipeline,
  firstYearRetention,
  retentionCohortSize,
  priorRetention,
  criticalRiskCount,
  pipelineStallCount,
  freshnessBreachCount,
}: SignalTilesProps) {
  const stale = trendState.status === "stale";
  const staleNote = stale
    ? trendState.asOfDay
      ? `stale ${trendState.asOfDay}`
      : "no data"
    : undefined;
  const note = comparisonNote(trendState, priorSnapshotDay);

  return (
    <section aria-describedby={stale ? AGG_STALE_BANNER_ID : undefined}>
      {stale && trendState.message && (
        <Banner
          variant="warn"
          title="Aggregate data is not current"
          className="mb-3"
        >
          <span id={AGG_STALE_BANNER_ID}>
            {trendState.message} Affects Active Members, In Pipeline and 1yr
            Retention above, and the FORGE, Apparatus &amp; Maintenance, IRONS,
            Membership, Community Engagement, Logistics &amp; Equipment and
            Board of Directors committee tiles below. Critical Risk, Pipeline
            Stalls, Operations and Data Freshness read other sources and are
            unaffected.
          </span>
        </Banner>
      )}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <KPICard
          label="Active Members"
          value={fmtNum(activeMembers)}
          subtitle={subtitleOf(
            note,
            totalMembers != null
              ? `of ${totalMembers.toLocaleString()} total`
              : null,
          )}
          trend={
            trendState.showTrends
              ? deltaTrend(activeMembers, priorActiveMembers, {
                  positiveIsGood: true,
                })
              : undefined
          }
          staleNote={staleNote}
        />
        <KPICard
          label="In Pipeline"
          value={fmtNum(applicantsInPipeline)}
          subtitle={subtitleOf(
            note,
            applicantsYtd != null ? `${applicantsYtd} YTD applications` : null,
          )}
          trend={
            trendState.showTrends
              ? deltaTrend(applicantsInPipeline, priorApplicantsPipeline, {
                  positiveIsGood: true,
                })
              : undefined
          }
          staleNote={staleNote}
        />
        <KPICard
          label="Critical Risk"
          value={String(criticalRiskCount)}
          accent={criticalRiskCount > 0 ? "red" : "neutral"}
        />
        <KPICard
          label="Pipeline Stalls"
          value={String(pipelineStallCount)}
          accent={pipelineStallCount > 0 ? "amber" : "neutral"}
        />
        <KPICard
          label="1yr Retention"
          value={fmtPct(firstYearRetention)}
          subtitle={subtitleOf(
            note,
            retentionCohortSize != null
              ? `cohort n=${retentionCohortSize}`
              : null,
          )}
          accent={
            firstYearRetention != null && firstYearRetention < 0.45
              ? "amber"
              : "neutral"
          }
          trend={
            trendState.showTrends
              ? deltaTrend(firstYearRetention, priorRetention, {
                  format: "percent_point",
                  positiveIsGood: true,
                })
              : undefined
          }
          staleNote={staleNote}
        />
        <KPICard
          label="Data Freshness"
          value={
            freshnessBreachCount === 0
              ? "On SLO"
              : `${freshnessBreachCount} breach`
          }
          accent={freshnessBreachCount > 0 ? "red" : "green"}
        />
      </div>
    </section>
  );
}

interface FreshnessSource {
  source_id: string;
  display_name: string;
  slo_hours: number;
  lag_hours: number | null;
  status: "ok" | "warn" | "breach" | "never_synced";
}

interface FreshnessResponse {
  sources: FreshnessSource[];
}

// ── committee tile model ─────────────────────────────────────────────────────

interface CommitteeTile {
  key: string;
  label: string;
  section: "operations" | "administration";
  to: string;
  primary: { label: string; value: string };
  secondary?: { label: string; value: string };
  /** Colored dot status. 'muted' = not yet instrumented. */
  status?: "ok" | "warn" | "alert" | "muted";
  note?: string;
}

function Dot({ status }: { status: CommitteeTile["status"] }) {
  const color =
    status === "alert"
      ? "bg-red-500"
      : status === "warn"
        ? "bg-amber-500"
        : status === "ok"
          ? "bg-emerald-500"
          : "bg-theme-muted/30";
  return <span className={`inline-block w-2 h-2 rounded-full ${color}`} />;
}

function CommitteeCard({ tile }: { tile: CommitteeTile }) {
  const dim = tile.status === "muted";
  return (
    <Link
      to={tile.to}
      className={`glass rounded-xl p-4 flex flex-col gap-3 hover:bg-theme-overlay/40 transition-colors border border-transparent hover:border-theme-subtle ${dim ? "opacity-70" : ""}`}
    >
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold text-theme-muted tracking-wide uppercase">
          {tile.label}
        </p>
        <Dot status={tile.status} />
      </div>
      <div>
        <p className="text-[11px] uppercase tracking-wide text-theme-muted">
          {tile.primary.label}
        </p>
        <p
          className={`text-2xl font-bold tabular-nums leading-tight ${dim ? "text-theme-muted" : "text-theme-primary"}`}
        >
          {tile.primary.value}
        </p>
      </div>
      {tile.secondary && (
        <div>
          <p className="text-[11px] uppercase tracking-wide text-theme-muted">
            {tile.secondary.label}
          </p>
          <p className="text-sm font-semibold text-theme-secondary tabular-nums">
            {tile.secondary.value}
          </p>
        </div>
      )}
      {tile.note && (
        <p className="text-xs text-theme-muted mt-auto">{tile.note}</p>
      )}
    </Link>
  );
}

// ── main ─────────────────────────────────────────────────────────────────────

export default function Dashboard() {
  const { data: agg } = useAggregates(60_000, true);
  const { data: riskSummary } = useRiskSummary();
  const { data: stalls } = usePipelineStalls();
  const { data: insights } = useInsights();
  const freshness = useFreshness();

  const aggregates = agg?.aggregates ?? {};
  const prior = agg?.prior ?? {};
  const priorSnapshotDay = agg?.prior_snapshot_day ?? null;
  const updatedAt = agg?.updated_at ?? null;

  // Gate for everything sourced from dash_aggregates. See aggregatesTrendState.
  const trendState = aggregatesTrendState(agg);

  // ── Real aggregate keys (verified against D1 on 2026-04-19) ─────────────
  const activeMembers = numOrNull(aggregates["active_member_count"]); // 156
  const totalMembers = numOrNull(aggregates["total_member_count"]); // 1875
  const applicantsInPipeline = numOrNull(aggregates["pipeline_active_current"]); // 119
  const applicantsYtd = numOrNull(aggregates["applicant_count_ytd"]); // 85
  const firstYearRetention = numOrNull(aggregates["first_year_retention_rate"]); // 0.3846
  const retentionCohortSize = numOrNull(
    aggregates["first_year_retention_cohort_size"],
  ); // 52
  const ytdJoined = numOrNull(aggregates["ytd_joined"]); // 9
  const ytdSeparated = numOrNull(aggregates["ytd_separated"]); // 9
  const _stuckApplicants = numOrNull(aggregates["stuck_applicants_count"]); // 2073
  const bodApprovalRate = numOrNull(aggregates["bod_approval_rate"]); // 0.72
  const bodApprovedYtd = numOrNull(aggregates["bod_approved_ytd"]); // 8
  const fleetInService = numOrNull(aggregates["fleet_in_service_count"]); // 38
  const fleetTotal = numOrNull(aggregates["fleet_total_count"]); // 63
  const fleetOos =
    fleetTotal != null && fleetInService != null
      ? fleetTotal - fleetInService
      : null;
  const openIrons = numOrNull(aggregates["open_irons_count"]); // 158
  const avgIronsResolutionD = numOrNull(
    aggregates["avg_irons_resolution_days"],
  ); // 26
  const communityEventsYtd = numOrNull(aggregates["community_events_ytd"]); // 5
  const communityReachYtd = numOrNull(aggregates["community_reach_ytd"]); // 32050
  const _activeRecruits = numOrNull(aggregates["active_recruits_in_training"]); // 43
  const _trainingCompletion = numOrNull(aggregates["training_completion_rate"]); // 0.458
  const emsStockCompliance = numOrNull(aggregates["ems_stock_compliance"]); // 1

  // Prior values from ~28 days ago (when /api/dashboard/aggregates?include_prior=1
  // is asked). Returns null until the snapshot history has 21+ days of data.
  const priorActiveMembers = numOrNull(prior["active_member_count"]);
  const priorApplicantsPipeline = numOrNull(prior["pipeline_active_current"]);
  const priorRetention = numOrNull(prior["first_year_retention_rate"]);

  const criticalRiskCount = riskSummary?.critical ?? 0;
  const pipelineStallCount = stalls?.count ?? 0;
  const freshnessBreachCount = (freshness?.sources ?? []).filter(
    (s) => s.status === "breach" || s.status === "never_synced",
  ).length;

  const tiles: CommitteeTile[] = useMemo(() => {
    return [
      {
        key: "forge",
        label: "FORGE",
        section: "operations",
        to: "/forge",
        primary: { label: "In Pipeline", value: fmtNum(applicantsInPipeline) },
        secondary: {
          label: `1yr Retention (n=${retentionCohortSize ?? 0})`,
          value: fmtPct(firstYearRetention),
        },
        status:
          firstYearRetention != null && firstYearRetention < 0.45
            ? "warn"
            : "ok",
      },
      {
        key: "operations",
        label: "Operations",
        section: "operations",
        to: "/staffing",
        primary: {
          label: "Critical-Risk Members",
          value: String(criticalRiskCount),
        },
        secondary: {
          label: "Pipeline Stalls",
          value: String(pipelineStallCount),
        },
        status:
          criticalRiskCount > 0 || pipelineStallCount > 5 ? "alert" : "ok",
      },
      {
        key: "apparatus",
        label: "Apparatus & Maintenance",
        section: "operations",
        to: "/fleet",
        primary: {
          label: "In Service",
          value:
            fleetInService != null && fleetTotal != null
              ? `${fleetInService} / ${fleetTotal}`
              : "Not yet tracked",
        },
        secondary: {
          label: "Out of Service",
          value: fleetOos != null ? String(fleetOos) : "—",
        },
        status: fleetOos != null && fleetOos > 0 ? "warn" : "ok",
      },
      {
        key: "irons",
        label: "IRONS",
        section: "operations",
        to: "/irons",
        primary: { label: "Open Issues", value: fmtNum(openIrons) },
        secondary:
          avgIronsResolutionD != null
            ? {
                label: "Avg Resolution",
                value: `${Math.round(avgIronsResolutionD)}d`,
              }
            : undefined,
        status:
          openIrons != null && openIrons > 100
            ? "alert"
            : openIrons != null && openIrons > 0
              ? "warn"
              : "ok",
      },
      {
        key: "membership",
        label: "Membership",
        section: "administration",
        to: "/membership",
        primary: { label: "Active", value: fmtNum(activeMembers) },
        secondary: {
          label: "YTD Joined / Separated",
          value:
            ytdJoined != null && ytdSeparated != null
              ? `${ytdJoined} / ${ytdSeparated}`
              : "—",
        },
        status:
          ytdJoined != null && ytdSeparated != null && ytdSeparated > ytdJoined
            ? "warn"
            : "ok",
      },
      {
        key: "bylaws",
        label: "Bylaws & Policy",
        section: "administration",
        to: "/memory",
        primary: { label: "Institutional Memory", value: "Searchable corpus" },
        secondary: { label: "Source", value: "25yr BOD minutes + SharePoint" },
        status: "ok",
      },
      {
        key: "community-engagement",
        label: "Community Engagement",
        section: "administration",
        to: "/community",
        primary: { label: "Events YTD", value: fmtNum(communityEventsYtd) },
        secondary:
          communityReachYtd != null
            ? { label: "Reach YTD", value: fmtNum(communityReachYtd) }
            : undefined,
        status: "ok",
      },
      {
        key: "logistics",
        label: "Logistics & Equipment",
        section: "administration",
        to: "/equipment",
        primary: {
          label: "EMS Stock Compliance",
          value:
            emsStockCompliance != null
              ? fmtPct(emsStockCompliance)
              : "Not yet tracked",
        },
        status:
          emsStockCompliance != null && emsStockCompliance < 0.95
            ? "warn"
            : "ok",
      },
      {
        key: "board",
        label: "Board of Directors",
        section: "administration",
        to: "/viability",
        primary: { label: "BOD Approved YTD", value: fmtNum(bodApprovedYtd) },
        secondary: { label: "Approval Rate", value: fmtPct(bodApprovalRate) },
        status: "ok",
      },
      {
        key: "hr",
        label: "Human Resources",
        section: "administration",
        to: "/hr/career-staff",
        primary: { label: "Access", value: "Board Members only" },
        note: "Gated surface",
        status: "muted",
      },
      {
        key: "insurance",
        label: "Insurance",
        section: "administration",
        to: "/insurance/coverage",
        primary: { label: "Coverage", value: "Not yet tracked" },
        note: "Pending data source",
        status: "muted",
      },
      {
        key: "ways-means",
        label: "Ways & Means",
        section: "administration",
        to: "/ways-means/fundraising",
        primary: { label: "Fundraising", value: "Not yet tracked" },
        note: "Pending data source",
        status: "muted",
      },
    ];
  }, [
    applicantsInPipeline,
    firstYearRetention,
    retentionCohortSize,
    activeMembers,
    criticalRiskCount,
    pipelineStallCount,
    fleetInService,
    fleetTotal,
    fleetOos,
    openIrons,
    avgIronsResolutionD,
    ytdJoined,
    ytdSeparated,
    bodApprovalRate,
    bodApprovedYtd,
    communityEventsYtd,
    communityReachYtd,
    emsStockCompliance,
  ]);

  const operationsTiles = tiles.filter((t) => t.section === "operations");
  const administrationTiles = tiles.filter(
    (t) => t.section === "administration",
  );

  return (
    <div className="p-4 md:p-6 lg:p-8 max-w-7xl mx-auto space-y-8">
      {/* Header */}
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="text-[11px] font-bold tracking-[0.14em] uppercase text-brand-adaptive mb-1">
            DCVFD
          </p>
          <h1 className="text-2xl font-black text-theme-primary tracking-tight">
            Executive Overview
          </h1>
          <p className="text-theme-secondary mt-1 text-sm">
            Department-wide health across operations and administration. All
            numbers sourced from D1 aggregates as of the date shown.
          </p>
        </div>
        <LiveIndicator lastUpdatedAt={updatedAt ? new Date(updatedAt) : null} />
      </header>

      {/* 1. Signal tiles */}
      <SignalTiles
        trendState={trendState}
        priorSnapshotDay={priorSnapshotDay}
        activeMembers={activeMembers}
        totalMembers={totalMembers}
        priorActiveMembers={priorActiveMembers}
        applicantsInPipeline={applicantsInPipeline}
        applicantsYtd={applicantsYtd}
        priorApplicantsPipeline={priorApplicantsPipeline}
        criticalRiskCount={criticalRiskCount}
        pipelineStallCount={pipelineStallCount}
        firstYearRetention={firstYearRetention}
        retentionCohortSize={retentionCohortSize}
        priorRetention={priorRetention}
        freshnessBreachCount={freshnessBreachCount}
      />

      {/* 2. AI insights */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-bold tracking-[0.12em] uppercase text-theme-secondary">
            What Changed
          </h2>
          <Link
            to="/memory"
            className="text-xs font-medium text-brand-adaptive hover:underline"
          >
            Institutional Memory →
          </Link>
        </div>
        <InsightCards insights={insights?.insights ?? null} />
      </section>

      {/* 3. Operations */}
      <section>
        <h2 className="text-sm font-bold tracking-[0.12em] uppercase text-brand-adaptive mb-3">
          Operations
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          {operationsTiles.map((t) => (
            <CommitteeCard key={t.key} tile={t} />
          ))}
        </div>
      </section>

      {/* 4. Administration */}
      <section>
        <h2 className="text-sm font-bold tracking-[0.12em] uppercase text-brand-adaptive mb-3">
          Administration
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          {administrationTiles.map((t) => (
            <CommitteeCard key={t.key} tile={t} />
          ))}
        </div>
      </section>

      {/* 5. Data Sources strip */}
      <section className="glass rounded-xl p-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-bold tracking-[0.12em] uppercase text-theme-secondary">
            Data Sources
          </h2>
          <Link
            to="/admin/ai"
            className="text-xs font-medium text-brand-adaptive hover:underline"
          >
            Admin →
          </Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {(freshness?.sources ?? []).map((s) => {
            const color =
              s.status === "ok"
                ? "bg-emerald-500"
                : s.status === "warn"
                  ? "bg-amber-500"
                  : "bg-red-500";
            const lag =
              s.lag_hours == null
                ? "never synced"
                : s.lag_hours < 1
                  ? `${Math.round(s.lag_hours * 60)}m`
                  : `${s.lag_hours.toFixed(1)}h`;
            return (
              <div
                key={s.source_id}
                className="flex items-center gap-2 text-xs"
              >
                <span
                  className={`inline-block w-1.5 h-1.5 rounded-full ${color}`}
                />
                <span className="font-semibold text-theme-secondary truncate">
                  {s.display_name}
                </span>
                <span className="ml-auto font-mono text-theme-muted shrink-0">
                  {lag}
                </span>
              </div>
            );
          })}
        </div>
      </section>

      {/* Honesty footer — what's still not instrumented */}
      <section className="text-xs text-theme-muted">
        <p>
          Three committees ({"\u201C"}muted{"\u201D"} above) have no live data
          source yet — their cards are intentional placeholders, not failed
          queries. Adding them is a schema task, not a bug.
        </p>
      </section>
    </div>
  );
}

// ── local hook — data freshness ──────────────────────────────────────────────

function useFreshness(): FreshnessResponse | null {
  const [data, setData] = useState<FreshnessResponse | null>(null);
  useEffect(() => {
    let cancelled = false;
    const tick = () => {
      fetch("/api/ops/data-freshness", { credentials: "same-origin" })
        .then((r) => (r.ok ? (r.json() as Promise<FreshnessResponse>) : null))
        .then((d) => {
          if (!cancelled) setData(d);
        })
        .catch(() => {
          if (!cancelled) setData(null);
        });
    };
    tick();
    const id = setInterval(tick, 5 * 60_000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);
  return data;
}
